import React from "react";
import { Box, Button, Card, Chip, Stack, Typography } from "@mui/material";
import LoadingState from "../../../components/common/LoadingState";
import ConversationContextStrip from "./ConversationContextStrip";
import ConversationHeader from "./ConversationHeader";
import MessageComposer from "./MessageComposer";
import MessageList from "./MessageList";

export default function ConversationPanel({
  thread,
  loading = false,
  composerValue,
  onComposerChange,
  onSend,
  composerError,
  encouragementOptions = [],
  encouragementLoading = false,
  onLoadEncouragement,
  onUseEncouragement,
  sending,
  onOpenSprint,
  onStartSprint,
  startingSprint = false,
  onOpenPerson,
}) {
  const messageScrollRef = React.useRef(null);
  const messageCount = thread?.messages?.length || 0;
  const connectionId = thread?.connectionId || "";
  const hasThread = Boolean(thread);

  React.useEffect(() => {
    const node = messageScrollRef.current;
    if (!node || loading || !hasThread) return;
    requestAnimationFrame(() => {
      node.scrollTop = node.scrollHeight;
    });
  }, [connectionId, hasThread, loading, messageCount]);

  return (
    <Card
      variant="outlined"
      sx={{
        minHeight: 640,
        borderRadius: "8px",
        borderColor: "rgba(226, 234, 242, 0.96)",
        backgroundColor: "#ffffff",
        boxShadow: "0 18px 40px rgba(15, 23, 42, 0.05)",
      }}
    >
      {!thread ? (
        <Stack spacing={1.2} sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: 800, color: "#14213d" }}>Select a conversation</Typography>
          <Typography variant="body2" sx={{ color: "#718198" }}>
            Your direct conversations live here, and active Accountability messages stay connected to the same thread.
          </Typography>
        </Stack>
      ) : loading ? (
        <Box sx={{ p: 2.2 }}>
          <LoadingState
            title="Opening conversation"
            body="Bringing messages and Accountability context into this thread."
            rows={4}
            compact
          />
        </Box>
      ) : (
        <Stack spacing={2} sx={{ p: 2.2 }}>
          <ConversationHeader partner={thread.partner} onOpenPerson={() => onOpenPerson?.(thread)} />
          <ConversationContextStrip
            activeSprint={thread.activeSprint}
            context={thread.context}
            onOpenSprint={onOpenSprint}
            onStartSprint={onStartSprint}
            startingSprint={startingSprint}
          />
          <Box ref={messageScrollRef} sx={{ minHeight: 380, maxHeight: 520, overflowY: "auto", pr: 0.5 }}>
            <MessageList messages={thread.messages || []} partner={thread.partner} onOpenPartner={() => onOpenPerson?.(thread)} />
          </Box>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: "#718198", fontWeight: 800 }}>
                Insights encouragement
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={onLoadEncouragement}
                disabled={encouragementLoading}
                sx={{ textTransform: "none" }}
              >
                {encouragementLoading ? "Loading..." : "Suggest options"}
              </Button>
            </Stack>
            {encouragementOptions.length ? (
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                {encouragementOptions.map((option) => (
                  <Chip
                    key={option}
                    label={option}
                    onClick={() => onUseEncouragement?.(option)}
                    sx={{
                      maxWidth: "100%",
                      height: "auto",
                      py: 0.6,
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        lineHeight: 1.35,
                      },
                    }}
                  />
                ))}
              </Stack>
            ) : null}
          </Stack>
          <MessageComposer
            value={composerValue}
            onChange={onComposerChange}
            onSend={onSend}
            error={composerError}
            sending={sending}
          />
        </Stack>
      )}
    </Card>
  );
}
